import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useRole } from "@/lib/roles";
import { OrderFormDialog } from "@/components/orders/OrderFormDialog";
import { OrderRow } from "@/components/orders/OrderRow";
import { OrderCard } from "@/components/orders/OrderCard";
import PdfLoadingOverlay from "@/components/PdfLoadingOverlay";

const BLANK = { job_number: "", description: "", active: true };

export default function Orders() {
  const { canWrite, canDelete } = useRole();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setError("");
    setOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ job_number: o.job_number, description: o.description || "", active: o.active });
    setError("");
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) {
        await api.put(`/orders/${editing.id}`, form);
        toast.success("Order updated");
      } else {
        await api.post("/orders", form);
        toast.success("Order created");
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const onDelete = async (o) => {
    if (!window.confirm(`Delete order ${o.job_number}?`)) return;
    await api.delete(`/orders/${o.id}`);
    toast.success("Order deleted");
    load();
  };

  const downloadJobReport = async (jobNumber, kind, { includeCertificates = true } = {}) => {
    const token = localStorage.getItem("jackstone_token");
    let url = `${API}/reports/jobs/${encodeURIComponent(jobNumber)}.${kind}`;
    if (kind === "pdf") {
      url += `?include_certificates=${includeCertificates ? "true" : "false"}`;
    }
    if (kind === "pdf") setPdfLoading(true);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        toast.error("Could not generate report");
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const suffix = kind === "pdf" ? (includeCertificates ? "report" : "summary") : "report";
      a.download = `Jackstone-Job-${jobNumber}-${suffix}.${kind}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error("Could not generate report");
    } finally {
      if (kind === "pdf") setPdfLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#39678f] subtitle mb-2">
            Active Orders
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
            Jobs & freezers
          </h1>
          <p className="text-sm text-[#50493b] mt-1">
            Manage the list of job numbers available to pressure tests.
          </p>
        </div>
        <OrderFormDialog
          open={open}
          onOpenChange={setOpen}
          onNew={openNew}
          editing={editing}
          form={form}
          setForm={setForm}
          error={error}
          onSubmit={onSubmit}
        />
      </div>

      <div className="hidden xl:block bg-white border border-[#33424f]/20 overflow-x-auto">
        <table className="w-full data-table text-sm min-w-[640px]">
          <thead>
            <tr>
              <th className="text-left px-6 py-3">Job number</th>
              <th className="text-left px-6 py-3">Description</th>
              <th className="text-left px-6 py-3">Active</th>
              <th className="text-right px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="orders-table-body">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-6 py-10 text-center text-[#50493b]">
                  Loading…
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-[#50493b]">
                  No orders yet.
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  canWrite={canWrite}
                  canDelete={canDelete}
                  openEdit={openEdit}
                  onDelete={onDelete}
                  downloadJobReport={downloadJobReport}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="xl:hidden bg-white border border-[#33424f]/20 divide-y divide-[#33424f]/10" data-testid="orders-card-list">
        {loading ? (
          <div className="px-4 py-10 text-center text-[#50493b] text-sm">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="px-4 py-12 text-center text-[#50493b] text-sm">No orders yet.</div>
        ) : (
          orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              canWrite={canWrite}
              canDelete={canDelete}
              openEdit={openEdit}
              onDelete={onDelete}
            />
          ))
        )}
      </div>

      <PdfLoadingOverlay open={pdfLoading} message="Compiling the job report — collating certificates can take a moment." />
    </div>
  );
}
